import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext();

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts(prev => [...prev, toast]);

    // Auto-remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const success = useCallback((message, duration) => showToast(message, 'success', duration), [showToast]);
  const error = useCallback((message, duration) => showToast(message, 'error', duration), [showToast]);
  const warning = useCallback((message, duration) => showToast(message, 'warning', duration), [showToast]);
  const info = useCallback((message, duration) => showToast(message, 'info', duration), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }) {
  // Always mounted, even when empty: a live region that appears together with
  // its first message is often not announced. Empty, it takes up no space.
  return (
    // Bottom-right, so it never sits on the header or the race bar; polite
    // live region so screen readers hear it (UI QA 2026-10-28, A2/F10).
    <div role="status" aria-live="polite" className="ff-toasts" style={{
      position: 'fixed',
      bottom: '20px',
      right: '16px',
      left: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      maxWidth: 'min(400px, calc(100vw - 32px))'
    }}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }) {
  const typeStyles = {
    success: {
      background: '#2B634B',
      icon: '✓'
    },
    error: {
      background: '#B91C1C',
      icon: '✕'
    },
    warning: {
      background: '#B45309',
      icon: '⚠'
    },
    info: {
      background: '#1D4ED8',
      icon: 'ℹ'
    }
  };

  const style = typeStyles[toast.type] || typeStyles.info;

  return (
    <div style={{
      background: style.background,
      color: '#FFFFFF',
      padding: '16px 20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: 'min(300px, calc(100vw - 32px))',
      animation: 'slideInRight 0.3s ease-out',
      position: 'relative'
    }}>
      <div style={{
        fontSize: '18px',
        fontWeight: 'bold',
        flexShrink: 0
      }}>
        {style.icon}
      </div>
      <div style={{
        flex: 1,
        fontSize: '14px',
        lineHeight: '1.5'
      }}>
        {toast.message}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onRemove(toast.id)}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#FFFFFF',
          cursor: 'pointer',
          fontSize: '20px',
          padding: '0',
          width: '36px',
          height: '36px',
          margin: '-6px -8px -6px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.8,
          flexShrink: 0
        }}
        onMouseEnter={(e) => e.target.style.opacity = '1'}
        onMouseLeave={(e) => e.target.style.opacity = '0.8'}
      >
        ×
      </button>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .ff-toasts, .ff-toasts * { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
